import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import { Widget } from '../../../types/canvas.types';

interface WidgetWrapperProps {
  widget: Widget;
  children: React.ReactNode;
  onEdit?: (widgetId: string) => void;
  onDelete?: (widgetId: string) => void;
  onDuplicate?: (widgetId: string) => void;
  onSettings?: (widgetId: string) => void;
}

export const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  widget,
  children,
  onEdit,
  onDelete,
  onDuplicate,
  onSettings,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const title =
    widget.configuration?.['title'] || `${widget.widget_type} Widget`;
  const description = widget.configuration?.['description'];

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover .widget-menu': {
          opacity: 1,
        },
      }}
    >
      <CardHeader
        title={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" component="h3">
              {title}
            </Typography>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              className="widget-menu"
              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        }
        subheader={description}
        sx={{ pb: 1 }}
      />

      <CardContent sx={{ flexGrow: 1, pt: 0 }}>{children}</CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {onSettings && (
          <MenuItem
            onClick={() => handleMenuAction(() => onSettings(widget.id))}
          >
            <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
            Settings
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={() => handleMenuAction(() => onEdit(widget.id))}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        {onDuplicate && (
          <MenuItem
            onClick={() => handleMenuAction(() => onDuplicate(widget.id))}
          >
            <DuplicateIcon sx={{ mr: 1 }} fontSize="small" />
            Duplicate
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={() => handleMenuAction(() => onDelete(widget.id))}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};
